/**
 * E04-F02-T05 — the "Link email to project" dialog.
 *
 * 1:1 port of the legacy `inbox.js` showLinkEmailDialog + createProjectFromEmail +
 * unlinkEmailFromProject. A dialog whose action set depends on the email's current
 * linkage (acceptance #2):
 *   - UNLINKED email → "Link to Project" (needs a selected ACTIVE project) +
 *     "Create Project" (confirm → create_from_email → openProject);
 *   - LINKED email → "Open Project" (host) + "Unlink" (confirm → unlink).
 *
 * Reply lives on the email preview action row (InboxView), not in this popup —
 * removed here per client request (E04-F02-T11).
 *
 * The project picker is an autocomplete over ACTIVE projects only, labelled
 * `role_name (client_name)`, searchable by role + client; the dropdown closes on a
 * click outside (legacy outside-click handler). Link disables + shows "Linking…"
 * while the request is in flight; success → toast + the feed reloads (the mutation
 * invalidates the emails domain).
 *
 * The filtering/labelling rules live in `inboxActionsModel.ts` (unit-tested). No
 * `innerHTML` — every project value is React text.
 */
import { useEffect, useMemo, useRef, useState, type JSX } from 'react';
import type { EmailDTO, ProjectListItemDTO } from '@eer/shared';
import { Dialog } from '../ui/Dialog.js';
import { useConfirm } from '../ui/ConfirmDialog.js';
import { useToast } from '../ui/ToastContext.js';
import { useProjects } from '../project/useProjects.js';
import { filterLinkProjects, projectLinkLabel } from './inboxActionsModel.js';
import { useLinkEmail, useUnlinkEmail, useCreateProjectFromEmail } from './inboxActionsApi.js';

export interface LinkEmailDialogProps {
  readonly email: EmailDTO;
  readonly open: boolean;
  readonly onClose: () => void;
  /** Open a project by id (host routes to the project detail) — "Open Project". */
  readonly onOpenProject?: (projectId: number) => void;
}

export function LinkEmailDialog({
  email,
  open,
  onClose,
  onOpenProject,
}: LinkEmailDialogProps): JSX.Element {
  const toast = useToast();
  const { confirm, confirmDialog } = useConfirm();
  const projectsQuery = useProjects();
  const link = useLinkEmail();
  const unlink = useUnlinkEmail();
  const create = useCreateProjectFromEmail();

  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<ProjectListItemDTO | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);

  const linked = email.project_id != null;

  const options = useMemo(
    () => filterLinkProjects(projectsQuery.data ?? [], query),
    [projectsQuery.data, query],
  );

  // Close the dropdown on a click outside the picker (legacy outside-click).
  useEffect(() => {
    function onDocClick(e: MouseEvent): void {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  function pick(project: ProjectListItemDTO): void {
    setSelected(project);
    setQuery(projectLinkLabel(project));
    setDropdownOpen(false);
  }

  function handleLink(): void {
    if (!selected) {
      toast.error('Select a project');
      return;
    }
    link.mutate(
      { emailId: email.id, projectId: selected.id },
      {
        onSuccess: () => {
          toast.success('Email linked');
          onClose();
        },
        onError: (err) => toast.error(err.detail.message || 'Link failed'),
      },
    );
  }

  async function handleCreate(): Promise<void> {
    // Confirm before creating a project from the email (acceptance #4) — styled
    // in-app confirm, not the native window.confirm (E04-F02-T09).
    const ok = await confirm({
      title: 'Create project',
      message: 'Create a new project from this email?',
      confirmLabel: 'Create',
    });
    if (!ok) return;
    create.mutate(email.id, {
      onSuccess: (res) => {
        toast.success('Project created');
        onClose();
        onOpenProject?.(res.project_id); // openProject(created) — acceptance #5
      },
      onError: (err) => toast.error(err.detail.message || 'Create failed'),
    });
  }

  async function handleUnlink(): Promise<void> {
    // Destructive → danger-variant styled confirm (E04-F02-T09).
    const ok = await confirm({
      title: 'Unlink email',
      message: 'Unlink this email from its project?',
      confirmLabel: 'Unlink',
      variant: 'danger',
    });
    if (!ok) return;
    unlink.mutate(email.id, {
      onSuccess: () => {
        toast.success('Email unlinked');
        onClose();
      },
      onError: (err) => toast.error(err.detail.message || 'Unlink failed'),
    });
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        title={linked ? 'Linked email' : 'Link email to a project'}
        testId="link-email-dialog"
        maxWidth="lg"
        footer={
          <div className="flex items-center gap-2">
            {linked ? (
              <>
                <button
                  type="button"
                  data-testid="link-email-open-project"
                  onClick={() => email.project_id != null && onOpenProject?.(email.project_id)}
                  className="rounded border border-[var(--color-border)] bg-white px-4 py-2 text-sm font-medium hover:bg-[var(--color-surface-muted)]"
                >
                  Open Project
                </button>
                <button
                  type="button"
                  data-testid="link-email-unlink"
                  onClick={() => void handleUnlink()}
                  disabled={unlink.isPending}
                  className="rounded bg-[var(--color-danger)] px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-60"
                >
                  {unlink.isPending ? 'Unlinking…' : 'Unlink'}
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  data-testid="link-email-create"
                  onClick={() => void handleCreate()}
                  disabled={create.isPending}
                  className="rounded border border-[var(--color-border)] bg-white px-4 py-2 text-sm font-medium hover:bg-[var(--color-surface-muted)] disabled:opacity-60"
                >
                  {create.isPending ? 'Creating…' : 'Create Project'}
                </button>
                <button
                  type="button"
                  data-testid="link-email-link"
                  onClick={handleLink}
                  disabled={link.isPending}
                  className="rounded bg-[var(--color-primary)] px-4 py-2 text-sm font-semibold text-white hover:bg-[var(--color-primary-hover)] disabled:opacity-60"
                >
                  {link.isPending ? 'Linking…' : 'Link to Project'}
                </button>
              </>
            )}
          </div>
        }
      >
        {linked ? (
          <p className="text-sm text-[var(--color-text)]" data-testid="link-email-linked-note">
            This email is already linked to a project. Open it or unlink it.
          </p>
        ) : (
          <div className="flex flex-col gap-2" ref={pickerRef}>
            <label
              className="text-sm font-medium text-[var(--color-text-strong)]"
              htmlFor="link-email-search"
            >
              Project
            </label>
            {/*
            In-flow (NOT position:absolute) so the suggestion list expands the
            dialog body within the panel's max-h-[90vh] instead of being clipped
            by the body's overflow-y-auto + panel's overflow-hidden (E04-F02-T10).
            The list keeps its own max-h-56 + overflow-y-auto so long result sets
            scroll INSIDE the dialog rather than pushing the footer off-screen.
          */}
            <div>
              <input
                id="link-email-search"
                type="text"
                data-testid="link-email-search"
                autoComplete="off"
                className="w-full rounded-md border border-[var(--color-border)] p-2 text-sm outline-none focus:border-[var(--color-accent)]"
                placeholder="Search by role or client…"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setSelected(null);
                  setDropdownOpen(true);
                }}
                onFocus={() => setDropdownOpen(true)}
              />
              {dropdownOpen ? (
                <ul
                  data-testid="link-email-options"
                  className="mt-1 max-h-56 w-full overflow-y-auto rounded-md border border-[var(--color-border)] bg-white"
                >
                  {options.length === 0 ? (
                    <li
                      className="px-3 py-2 text-xs text-[var(--color-text-muted)]"
                      data-testid="link-email-no-options"
                    >
                      No active projects match.
                    </li>
                  ) : (
                    options.map((p) => (
                      <li key={p.id}>
                        <button
                          type="button"
                          data-testid="link-email-option"
                          data-project-id={p.id}
                          onClick={() => pick(p)}
                          className="block w-full px-3 py-2 text-left text-sm hover:bg-[var(--color-surface-muted)]"
                        >
                          {projectLinkLabel(p)}
                        </button>
                      </li>
                    ))
                  )}
                </ul>
              ) : null}
            </div>
          </div>
        )}
      </Dialog>
      {confirmDialog}
    </>
  );
}

export default LinkEmailDialog;
